import {
  Box,
  Card,
  Container,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import React, { useEffect, useState } from 'react';
import { I_S3Credentials } from '../../../interfaces/S3Types';
import ReactApexChart from 'react-apexcharts';
import useIsMobile from '../../../hooks/useIsMobile';
import { getChartOptions } from '../Overview/chartOptions';
import {
  BatteryDataInterface,
  BatteryOverviewInterface,
  transformInputToBatteryViewDataJson
} from '../../../interfaces/Chart';
import dayjs, { Dayjs } from 'dayjs';
import { TimeSpan, UnixTime } from '../../../dataCache/time';
import Button from '@mui/material/Button';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CircularProgress from '@mui/material/CircularProgress';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ActiveCluster } from '../Information/installationSetupUtils';

interface MainStatsSodioHomeProps {
  s3Credentials: I_S3Credentials;
  id: number;
  batteryClusterNumber: number;
  activeClusters?: ActiveCluster[];
  showInverterLabel?: boolean;
}

function MainStatsSodioHome(props: MainStatsSodioHomeProps) {
  // Mobile: shorter charts to save vertical space (see plan 2.3).
  const isPhone = useIsMobile();
  const [chartState, setChartState] = useState(0);
  const [batteryViewDataArray, setBatteryViewDataArray] = useState<
    {
      chartData: BatteryDataInterface;
      chartOverview: BatteryOverviewInterface;
    }[]
  >([]);

  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(dayjs().add(-1, 'day'));
  const [endDate, setEndDate] = useState(dayjs());
  const [isErrorDateModalOpen, setErrorDateModalOpen] = useState(false);
  const [dateSelectionError, setDateSelectionError] = useState('');
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const blueColors = [
    '#99CCFF',
    '#80BFFF',
    '#6699CC',
    '#4D99FF',
    '#2670E6',
    '#3366CC',
    '#1A4D99',
    '#133366',
    '#0D274D',
    '#081A33'
  ];
  const redColors = [
    '#ff9090',
    '#ff7070',
    '#ff3f3f',
    '#ff1e1e',
    '#ff0606',
    '#fc0000',
    '#f40000',
    '#d40000',
    '#a30000',
    '#7a0000'
  ];
  const orangeColors = [
    '#ffdb99',
    '#ffc968',
    '#ffb837',
    '#ffac16',
    '#ffa706',
    '#FF8C00',
    '#d48900',
    '#CC7A00',
    '#a36900',
    '#993D00'
  ];
  const greenColors = [
    '#90EE90',
    '#77DD77',
    '#5ECE5E',
    '#45BF45',
    '#32CD32',
    '#28A428',
    '#1E7B1E',
    '#145214',
    '#0A390A',
    '#052905'
  ];

  useEffect(() => {
    setLoading(true);

    const resultPromise: Promise<{
      chartData: BatteryDataInterface;
      chartOverview: BatteryOverviewInterface;
    }> = transformInputToBatteryViewDataJson(
      props.s3Credentials,
      props.id,
      2,
      UnixTime.fromTicks(new Date().getTime() / 1000).earlier(
        TimeSpan.fromDays(1)
      ),
      UnixTime.fromTicks(new Date().getTime() / 1000),
      props.batteryClusterNumber,
      props.activeClusters
    );

    resultPromise
      .then((result) => {
        setBatteryViewDataArray((prevData) =>
          prevData.concat({
            chartData: result.chartData,
            chartOverview: result.chartOverview
          })
        );

        setLoading(false);
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  }, []);

  const [isZooming, setIsZooming] = useState(false);

  // Mobile zoom: phones cannot drag-select a range, so the Zoom out/in buttons
  // drive a real time-window zoom (anchored to now) instead of stepping through
  // drag-zoom history. Window in hours, clamped to [1h, 30d].
  const MOBILE_MIN_HOURS = 1;
  const MOBILE_MAX_HOURS = 24 * 30;
  const [mobileWindowHours, setMobileWindowHours] = useState(24);

  useEffect(() => {
    if (isZooming) {
      setLoading(true);
    } else if (!isZooming && batteryViewDataArray.length > 0) {
      setLoading(false);
    }
  }, [isZooming, batteryViewDataArray]);

  function generateSeries(chartData, category, color) {
    const series = [];
    const pathsToSearch: string[] = [];
    if (props.activeClusters && props.activeClusters.length > 0) {
      props.activeClusters.forEach((c) => {
        pathsToSearch.push('Node' + c.flatIdx);
      });
    } else {
      for (let i = 0; i < props.batteryClusterNumber; i++) {
        pathsToSearch.push('Node' + i);
      }
    }

    const total = pathsToSearch.length;
    let i = 0;
    pathsToSearch.forEach((devicePath) => {
      if (
        Object.hasOwnProperty.call(chartData[category].data, devicePath) &&
        chartData[category].data[devicePath].data.length != 0
      ) {
        // Spread color indices evenly across the palette for better contrast
        const colorIndex = total <= 1 ? 0 : Math.round(i * 9 / (total - 1));
        series.push({
          ...chartData[category].data[devicePath],
          color:
            color === 'blue'
              ? blueColors[colorIndex]
              : color === 'red'
              ? redColors[colorIndex]
              : color === 'green'
              ? greenColors[colorIndex]
              : orangeColors[colorIndex]
        });
      }
      i++;
    });

    return series;
  }

  const handleCancel = () => {
    setIsDateModalOpen(false);
    setDateOpen(false);
  };

  const handleConfirm = () => {
    setIsDateModalOpen(false);
    setDateOpen(false);

    if (endDate.isAfter(dayjs())) {
      setDateSelectionError('You cannot ask for future data');
      setErrorDateModalOpen(true);
      return;
    } else if (startDate.isAfter(endDate)) {
      setDateSelectionError('End date must precede start date');
      setErrorDateModalOpen(true);
      return;
    }

    setLoading(true);
    const resultPromise: Promise<{
      chartData: BatteryDataInterface;
      chartOverview: BatteryOverviewInterface;
    }> = transformInputToBatteryViewDataJson(
      props.s3Credentials,
      props.id,
      2,
      UnixTime.fromTicks(startDate.unix()),
      UnixTime.fromTicks(endDate.unix()),
      props.batteryClusterNumber,
      props.activeClusters
    );

    resultPromise
      .then((result) => {
        setBatteryViewDataArray((prevData) =>
          prevData.concat({
            chartData: result.chartData,
            chartOverview: result.chartOverview
          })
        );

        setLoading(false);
        setChartState(batteryViewDataArray.length);
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };
  const handleSetDate = () => {
    setDateOpen(true);
    setIsDateModalOpen(true);
  };

  const handleBatteryViewButton = () => {
    navigate(
      location.pathname.split('/').slice(0, -2).join('/') + '/batteryview'
    );
  };

  const handleGoBack = () => {
    if (chartState > 0) {
      setChartState(chartState - 1);
    }
  };

  const handleGoForward = () => {
    if (chartState + 1 < batteryViewDataArray.length) {
      setChartState(chartState + 1);
    }
  };

  // Zoom the time window by `factor` (2 = out/wider, 0.5 = in/narrower), anchored
  // to now, and fetch that range. Mobile-only entry point for the Zoom buttons.
  const handleMobileZoom = (factor: number) => {
    const newHours = Math.min(
      Math.max(mobileWindowHours * factor, MOBILE_MIN_HOURS),
      MOBILE_MAX_HOURS
    );
    setMobileWindowHours(newHours);
    setIsZooming(true);
    const now = UnixTime.fromTicks(new Date().getTime() / 1000);
    transformInputToBatteryViewDataJson(
      props.s3Credentials,
      props.id,
      2,
      now.earlier(TimeSpan.fromHours(newHours)),
      now,
      props.batteryClusterNumber,
      props.activeClusters
    )
      .then((result) => {
        setBatteryViewDataArray((prevData) => {
          const next = prevData.concat({
            chartData: result.chartData,
            chartOverview: result.chartOverview
          });
          setChartState(next.length - 1);
          return next;
        });
        setIsZooming(false);
      })
      .catch((error) => {
        console.error('Error:', error);
        setIsZooming(false);
      });
  };

  const handleOkOnErrorDateModal = () => {
    setErrorDateModalOpen(false);
  };

  const startZoom = () => {
    setIsZooming(true);
  };

  const handleBeforeZoom = (chartContext, { xaxis }) => {
    const startX = parseInt(xaxis.min) / 1000;
    const endX = parseInt(xaxis.max) / 1000;

    const resultPromise: Promise<{
      chartData: BatteryDataInterface;
      chartOverview: BatteryOverviewInterface;
    }> = transformInputToBatteryViewDataJson(
      props.s3Credentials,
      props.id,
      2,
      UnixTime.fromTicks(startX).earlier(TimeSpan.fromHours(2)),
      UnixTime.fromTicks(endX).earlier(TimeSpan.fromHours(2)),
      props.batteryClusterNumber,
      props.activeClusters
    );

    resultPromise
      .then((result) => {
        setBatteryViewDataArray((prevData) =>
          prevData.concat({
            chartData: result.chartData,
            chartOverview: result.chartOverview
          })
        );

        setIsZooming(false);
        setChartState(batteryViewDataArray.length);
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  return (
    <>
      {loading && (
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh'
          }}
        >
          <CircularProgress size={60} style={{ color: '#ffc04d' }} />
          <Typography variant="body2" style={{ color: 'black' }} mt={2}>
            <FormattedMessage id="fetchingData" defaultMessage="Fetching data..." />
          </Typography>
        </Container>
      )}
      {isErrorDateModalOpen && (
        <Modal open={isErrorDateModalOpen} onClose={() => {}}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90vw', sm: 450 },
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: 24,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              {dateSelectionError}
            </Typography>

            <Button
              sx={{
                marginTop: 2,
                textTransform: 'none',
                bgcolor: '#ffc04d',
                color: '#111111',
                '&:hover': { bgcolor: '#f7b34d' }
              }}
              onClick={handleOkOnErrorDateModal}
            >
              Ok
            </Button>
          </Box>
        </Modal>
      )}
      {isDateModalOpen && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Modal open={isDateModalOpen} onClose={() => {}}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '90vw', sm: 450 },
                bgcolor: 'background.paper',
                borderRadius: 4,
                boxShadow: 24,
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <DateTimePicker
                label="Select Start Date"
                value={startDate}
                onChange={(newDate: Dayjs | null) => {
                  if (newDate) {
                    setStartDate(newDate);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      marginTop: 2,
                      width: '100%'
                    }}
                  />
                )}
              />

              <DateTimePicker
                label="Select End Date"
                value={endDate}
                onChange={(newDate: Dayjs | null) => {
                  if (newDate) {
                    setEndDate(newDate);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      marginTop: 2,
                      width: '100%'
                    }}
                  />
                )}
              />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: 10
                }}
              >
                <Button
                  sx={{
                    marginTop: 2,
                    textTransform: 'none',
                    bgcolor: '#ffc04d',
                    color: '#111111',
                    '&:hover': { bgcolor: '#f7b34d' }
                  }}
                  onClick={handleConfirm}
                >
                  Confirm
                </Button>

                <Button
                  sx={{
                    marginTop: 2,
                    marginLeft: 2,
                    textTransform: 'none',
                    bgcolor: '#ffc04d',
                    color: '#111111',
                    '&:hover': { bgcolor: '#f7b34d' }
                  }}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </div>
            </Box>
          </Modal>
        </LocalizationProvider>
      )}

      {!loading && (
        <>
          <Grid item xs={6} md={6}>
            <IconButton
              aria-label="go back"
              sx={{
                marginTop: '20px',
                backgroundColor: 'grey',
                color: '#000000',
                '&:hover': { bgcolor: '#f7b34d' }
              }}
              onClick={handleBatteryViewButton}
            >
              <ArrowBackIcon />
            </IconButton>

            <Button
              variant="contained"
              onClick={handleSetDate}
              disabled={loading}
              sx={{
                marginTop: '20px',
                marginLeft: '20px',
                backgroundColor: dateOpen ? '#808080' : '#ffc04d',
                color: '#000000',
                '&:hover': { bgcolor: '#f7b34d' }
              }}
            >
              <FormattedMessage id="set_date" defaultMessage="Set Date" />
            </Button>
          </Grid>
          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            item
            xs={6}
            md={6}
          >
            <Button
              variant="contained"
              disabled={
                isPhone
                  ? mobileWindowHours >= MOBILE_MAX_HOURS
                  : !(chartState > 0)
              }
              onClick={isPhone ? () => handleMobileZoom(2) : handleGoBack}
              sx={{
                marginTop: '20px',
                marginLeft: '10px',
                backgroundColor: '#ffc04d',
                color: '#000000',
                '&:hover': { bgcolor: '#f7b34d' }
              }}
            >
              <FormattedMessage id="goback" defaultMessage="Zoom out" />
            </Button>

            <Button
              variant="contained"
              disabled={
                isPhone
                  ? mobileWindowHours <= MOBILE_MIN_HOURS
                  : !(chartState < batteryViewDataArray.length - 1)
              }
              onClick={isPhone ? () => handleMobileZoom(0.5) : handleGoForward}
              sx={{
                marginTop: '20px',
                marginLeft: '10px',
                backgroundColor: '#ffc04d',
                color: '#000000',
                '&:hover': { bgcolor: '#f7b34d' }
              }}
            >
              <FormattedMessage id="goback" defaultMessage="Zoom in" />
            </Button>
          </Grid>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={3}
          >
            {/* Battery SOC Chart */}
            <Grid item md={12} xs={12}>
              <Card
                sx={{
                  overflow: 'visible',
                  marginTop: '30px',
                  marginBottom: '30px'
                }}
              >
                <Box sx={{ marginLeft: '20px' }}>
                  <Box display="flex" alignItems="center">
                    <Box>
                      <Typography variant="subtitle1" noWrap>
                        <FormattedMessage
                          id="battery_soc"
                          defaultMessage="Battery SOC (State Of Charge)"
                        />
                        {isPhone ? ' (%)' : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      pt: 3
                    }}
                  ></Box>
                </Box>

                <ReactApexChart
                  options={{
                    ...getChartOptions(
                      batteryViewDataArray[chartState].chartOverview.Soc,
                      'daily',
                      [],
                      true
                    ),
                    chart: {
                      events: {
                        beforeZoom: (chartContext, options) => {
                          startZoom();
                          handleBeforeZoom(chartContext, options);
                        }
                      }
                    }
                  }}
                  series={generateSeries(
                    batteryViewDataArray[chartState].chartData,
                    'Soc',
                    'blue'
                  )}
                  type="line"
                  height={isPhone ? 280 : 420}
                />
              </Card>
            </Grid>

            {/* Battery Power Chart */}
            <Grid item md={12} xs={12}>
              <Card
                sx={{
                  overflow: 'visible',
                  marginTop: '10px',
                  marginBottom: '30px'
                }}
              >
                <Box sx={{ marginLeft: '20px' }}>
                  <Box display="flex" alignItems="center">
                    <Box>
                      <Typography variant="subtitle1" noWrap>
                        <FormattedMessage
                          id="battery_power"
                          defaultMessage="Battery Power"
                        />
                        {isPhone ? ' (kW)' : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      pt: 3
                    }}
                  ></Box>
                </Box>

                <ReactApexChart
                  options={{
                    ...getChartOptions(
                      batteryViewDataArray[chartState].chartOverview.Power,
                      'daily',
                      [],
                      true
                    ),
                    chart: {
                      events: {
                        beforeZoom: (chartContext, options) => {
                          startZoom();
                          handleBeforeZoom(chartContext, options);
                        }
                      }
                    }
                  }}
                  series={generateSeries(
                    batteryViewDataArray[chartState].chartData,
                    'Power',
                    'red'
                  )}
                  type="line"
                  height={isPhone ? 280 : 420}
                />
              </Card>
            </Grid>

            {/* Battery Voltage Chart */}
            <Grid item md={12} xs={12}>
              <Card
                sx={{
                  overflow: 'visible',
                  marginTop: '10px',
                  marginBottom: '30px'
                }}
              >
                <Box sx={{ marginLeft: '20px' }}>
                  <Box display="flex" alignItems="center">
                    <Box>
                      <Typography variant="subtitle1" noWrap>
                        <FormattedMessage
                          id="battery_voltage"
                          defaultMessage="Battery Voltage"
                        />
                        {isPhone ? ' (V)' : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      pt: 3
                    }}
                  ></Box>
                </Box>

                <ReactApexChart
                  options={{
                    ...getChartOptions(
                      batteryViewDataArray[chartState].chartOverview.Voltage,
                      'daily',
                      [],
                      true
                    ),
                    chart: {
                      events: {
                        beforeZoom: (chartContext, options) => {
                          startZoom();
                          handleBeforeZoom(chartContext, options);
                        }
                      }
                    }
                  }}
                  series={generateSeries(
                    batteryViewDataArray[chartState].chartData,
                    'Voltage',
                    'orange'
                  )}
                  type="line"
                  height={isPhone ? 280 : 420}
                />
              </Card>
            </Grid>

            {/* Battery Current Chart */}
            <Grid item md={12} xs={12}>
              <Card
                sx={{
                  overflow: 'visible',
                  marginTop: '10px',
                  marginBottom: '30px'
                }}
              >
                <Box sx={{ marginLeft: '20px' }}>
                  <Box display="flex" alignItems="center">
                    <Box>
                      <Typography variant="subtitle1" noWrap>
                        <FormattedMessage
                          id="battery_current"
                          defaultMessage="Battery Current"
                        />
                        {isPhone ? ' (A)' : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      pt: 3
                    }}
                  ></Box>
                </Box>

                <ReactApexChart
                  options={{
                    ...getChartOptions(
                      batteryViewDataArray[chartState].chartOverview.Current,
                      'daily',
                      [],
                      true
                    ),
                    chart: {
                      events: {
                        beforeZoom: (chartContext, options) => {
                          startZoom();
                          handleBeforeZoom(chartContext, options);
                        }
                      }
                    }
                  }}
                  series={generateSeries(
                    batteryViewDataArray[chartState].chartData,
                    'Current',
                    'orange'
                  )}
                  type="line"
                  height={isPhone ? 280 : 420}
                />
              </Card>
            </Grid>

            {/* Battery SoH Chart — removed for SodiostoreHome */}
          </Grid>
        </>
      )}
    </>
  );
}

export default MainStatsSodioHome;
